import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { companyInfo, navigation } from '../../data';
import Button from '../ui/Button';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'py-3 px-4 md:px-8' 
          : 'py-6 px-4 md:px-8'
      }`}
    >
      <div className={`max-w-7xl mx-auto relative overflow-hidden transition-all duration-500 ${
        scrolled 
          ? 'bg-[#1d398e] shadow-lg rounded-2xl border border-white/10' 
          : 'bg-[#1d398e]/90 backdrop-blur-sm rounded-2xl border border-white/10'
      }`}>
        {/* Skewed background for logo */}
        <div className="absolute top-0 left-0 h-full w-[40%] md:w-[30%] bg-white skew-x-[-15deg] -translate-x-12 z-0" />

        <div className="flex justify-between items-center h-16 px-6 relative z-10">
          <Link to="/" className="flex items-center group">
            <img 
              src="https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/1bf8ce2d-291e-4ca8-b7a4-98511f9c0ee1.png" 
              alt="NR. GALTEN VOGNMANDSFORRETNING ApS" 
              className="h-10 md:h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  location.pathname === item.href
                    ? 'bg-white text-[#1d398e] shadow-md'
                    : 'text-white/90 hover:bg-white/10 hover:text-white'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div className="ml-4 pl-4 border-l border-white/20">
              <Button 
                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                variant="accent"
                className="text-xs py-2 px-4 rounded-full border-none text-white shadow-sm hover:shadow-amber-500/20"
              >
                <Phone className="w-3 h-3 mr-2" />
                {companyInfo.phone}
              </Button>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-xl transition-colors text-white hover:bg-white/10"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-full left-4 right-4 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden lg:hidden"
          >
            <div className="flex flex-col p-4 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    location.pathname === item.href
                      ? 'bg-blue-50 text-blue-900'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4 px-4">
                <Button 
                  href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                  className="w-full justify-center"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {companyInfo.phone}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
